import React, { useState } from 'react';
import { HelpCircle, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Help tooltip component with detailed information
 * Hidden on mobile to prevent layout issues
 */
export function HelpTooltip({ title, content, position = 'right' }) {
  const [isOpen, setIsOpen] = useState(false);

  const positionClasses = {
    right: 'left-full ml-2 top-0',
    left: 'right-full mr-2 top-0',
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2'
  };

  return (
    <div className="relative inline-block hidden md:inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-400 hover:text-[#c5a059] transition"
        aria-label="Help"
      >
        <HelpCircle size={18} />
      </button>
      
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            className={`absolute z-50 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-gray-200 p-4 ${positionClasses[position]}`}
            style={{ maxWidth: 'min(320px, calc(100vw - 2rem))' }}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="text-sm text-gray-600 leading-relaxed">
              {content}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Feature guide component for explaining features
 * Collapsible to save space
 */
export function FeatureGuide({ title, description, steps = [], tips = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg mb-6 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-100 transition text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <HelpCircle className="text-blue-600" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 text-sm md:text-base">{title}</h3>
            {!isExpanded && (
              <p className="text-gray-600 text-xs md:text-sm mt-1 line-clamp-1">{description}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          {isExpanded ? (
            <ChevronUp className="text-gray-600" size={20} />
          ) : (
            <ChevronDown className="text-gray-600" size={20} />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">{description}</p>
          
          {steps.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 text-sm mb-2">How to use:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                {steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          
          {tips.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-800 text-sm mb-2">💡 Tips:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                {tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Info banner for important information
 * Collapsible to save space
 */
export function InfoBanner({ type = 'info', title, children }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800'
  };

  return (
    <div className={`border rounded-lg mb-6 overflow-hidden ${styles[type]}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:opacity-90 transition text-left"
      >
        <h4 className="font-semibold text-sm md:text-base">{title}</h4>
        <div className="flex-shrink-0 ml-2">
          {isExpanded ? (
            <ChevronUp className="text-current" size={18} />
          ) : (
            <ChevronDown className="text-current" size={18} />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 text-sm leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}

